import { api } from "_shared/_stores/api";
import { permissionCacheTags } from "permissions/_api/permission-cache-tags";
import type { CheckedRecordDomain } from "permissions/_views/records/record-permissions-path";
import { z } from "zod";

/**
 * Check access on a record page: can this person open this record, and which grants decide it.
 *
 * The answer is the server's, read through the same query the record's own reads assert (#6190), so
 * the page never works it out itself:
 *
 * - `GET /messaging/forums/{forumId}/access/{userId}`
 * - `GET /documents/{documentId}/access/{userId}`
 * - `GET /work/proposals/{proposalId}/access/{userId}`
 * - `GET /work/{workId}/access/{userId}`
 * - `GET /calendar/events/{eventId}/access/{userId}`
 * - `GET /legal-entities/entity-agreements/{agreementId}/access/{userId}`
 * - `GET /work/fee-schedule-templates/{templateId}/access/{userId}`
 * - `GET /work/team-templates/{templateId}/access/{userId}`
 *
 * The last four came with #6832. The caller needs entity-wide member permissions and may ask only
 * about their own organization's members, but need not be able to open the record. A person who
 * cannot open it is a 200 with `canOpen: false`. A conversation's answer has its own shape; every
 * other domain answers in `RecordAccessCheckResponse`'s. Both are read into `RecordAccessAnswer`.
 *
 * A "No" carries `remedies`: what would turn it into "Yes", and whether this viewer may make each change
 * from the answer (#6833). The server names only what the viewer may see.
 *
 * An action beyond Open is asked beside it, named for the action (#6830): `GET …/access/{userId}/edit`
 * on a conversation, a document or a proposal; `GET …/access/{userId}/approve` on a proposal
 * (#7153); `GET …/access/{userId}/invite` on a conversation and a proposal (#7151); and
 * `GET …/access/{userId}/sign` on a proposal (hire it), a work agreement (lead it) or an
 * organization agreement (accept it) (#7150). Every domain answers it in
 * `RecordActionCheckResponse`'s shape, read into `RecordActionAnswer`. Invite on the
 * organization has no record page; the person page asks
 * `GET /legal-entities/{entityId}/access/{userId}/invite` in the same shape.
 */

/** Why a grant opens a conversation, as `ForumAccessRules` names it on the server. */
export const ForumAccessRule = {
  /** A person granted it. */
  Direct: "direct",
  /** The system wrote it from the person's communications permission. */
  Parent: "parent",
  /** The system derived it from a roster the person is on; `source` names the roster. */
  Roster: "roster",
  /** The read-only grant kept after leaving the work the conversation belongs to. */
  LeftRoster: "leftRoster",
  /** The system wrote it with no parent and no roster: a work agreement chat's grant from before rosters. */
  System: "system",
  /** Not a grant: the person started the conversation, which is what lets them invite people into it (#7151). */
  Creator: "creator",
} as const;

/** Why a person can open a record other than a conversation, as `RecordAccessRules` names it on the server. */
export const RecordAccessRule = {
  Direct: "direct",
  /** The system wrote it with no parent and no relationship it names. */
  System: "system",
  /** The system wrote it from a standing grant the person holds. */
  Parent: "parent",
  /** The person leads the proposal, is on a team on it, or reviews it. */
  DerivedProposal: "derivedProposal",
  /** The person is on the work agreement's roster. */
  Roster: "roster",
  /** The person holds access for the whole organization, and that reaches this record. */
  EntityWide: "entityWide",
  /** The person left the work and keeps a read of it as it was when they left. */
  LeftRoster: "leftRoster",
  /** A grant the person holds through another organization. The server says nothing more about it. */
  OtherOrganization: "otherOrganization",
} as const;

/** What refuses a record the reasons reach, as `RecordAccessWithholdings` names it on the server. */
export const RecordAccessWithholding = {
  /** The document is a proposal field's contents, and the person's access to the proposal leaves the field out. */
  ProposalField: "proposalField",
  /** The proposal is an unsubmitted draft, and the person is not on the side drafting it. */
  UnsubmittedDraft: "unsubmittedDraft",
} as const;

/**
 * What Check access can ask about a record (#6830). Open is today's question, answered by
 * `RecordAccessAnswer`; every other action is answered by `RecordActionAnswer`, from the action's own
 * check on the server.
 */
export const RecordAction = {
  Open: "open",
  Edit: "edit",
  /** Invite people: into a conversation, or onto a proposal's teams and reviewers (#7151). */
  Invite: "invite",
  /** Approve a proposal: the approval its status waits for, or a reviewer's approval of their review (#7153). */
  Approve: "approve",
  /** Sign for the organization: hire a proposal, lead a work agreement, accept an organization agreement. */
  Sign: "sign",
} as const;
export type RecordAction = (typeof RecordAction)[keyof typeof RecordAction];

/** The actions each record type has, Open first. Only these are offered. */
export function recordActionsOf(domain: CheckedRecordDomain): readonly RecordAction[] {
  switch (domain) {
    case "proposals":
      return [RecordAction.Open, RecordAction.Edit, RecordAction.Invite, RecordAction.Approve, RecordAction.Sign];
    case "forums":
      return [RecordAction.Open, RecordAction.Edit, RecordAction.Invite];
    case "documents":
      return [RecordAction.Open, RecordAction.Edit];
    case "work-agreements":
    case "organization-agreements":
      return [RecordAction.Open, RecordAction.Sign];
    default:
      return [RecordAction.Open];
  }
}

/** What refuses an action on a record the person opens, as `RecordActionRefusals` names it on the server. */
export const RecordActionRefusal = {
  /** Every grant that reaches the record gives Can read only. */
  ReadOnly: "readOnly",
  /** The conversation's work has ended, or the person left it. */
  WorkEnded: "workEnded",
  /** A proposal's All Stakeholders thread, whose audience leaves the person out: a reviewer reads it and writes nothing. */
  OutsideAudience: "outsideAudience",
  /** Not the proposal's team leader, a reviewer or a member of a team on it. */
  NotOnProposal: "notOnProposal",
  /** A seat on both sides of the proposal. */
  RepresentsBothParties: "representsBothParties",
  /** A reviewer who is not one of the editing reviewers. */
  NotEditingReviewer: "notEditingReviewer",
  /** On a team on the proposal, and neither its leader nor a reviewer. */
  TeamMemberOnly: "teamMemberOnly",
  /** The proposal is archived, canceled or hired. */
  ProposalClosed: "proposalClosed",
  /** No Member invitations for the whole organization, at any level (#7151). */
  NoMemberInvitations: "noMemberInvitations",
  /** Somebody else started the conversation, or nobody it records. */
  NotConversationCreator: "notConversationCreator",
  /** On the proposal, and not one of its team leaders. */
  NotLeader: "notLeader",
  /** The proposal's status does not wait for the person's approval; `awaitedApprovers` names whose it does. */
  NotAwaitingTheirApproval: "notAwaitingTheirApproval",
  /** On a team on the proposal, not one of its reviewers, and only reviewers approve it now. */
  NotAssignedReviewer: "notAssignedReviewer",
  /** No signing authority for the organization. */
  NoSigningAuthority: "noSigningAuthority",
  /** Signing authority at a narrower scope than the action asks. */
  SigningAuthorityTooNarrow: "signingAuthorityTooNarrow",
  /** Only the proposal's client team leader hires it. */
  NotClientLeader: "notClientLeader",
  /** Another bid for the same work is already hired. */
  WorkAlreadyAwarded: "workAlreadyAwarded",
  /** Only the organization agreement's principal accepts it. */
  NotPrincipal: "notPrincipal",
  /** The organization agreement was already accepted or rejected. */
  AgreementNotOpen: "agreementNotOpen",
  /** The organization agreement names no location yet. */
  AgreementIncomplete: "agreementIncomplete",
} as const;

/** What would open a record the answer refuses, as `RecordAccessRemedyRules` names it on the server. */
export const RecordAccessRemedyRule = {
  /** A grant on the record itself. */
  RecordGrant: "recordGrant",
  /** A standing kind held for the whole organization; `permissionKind` names it. */
  StandingGrant: "standingGrant",
  /** A place on the work's roster, which the work's leader gives. Never offered. */
  Roster: "roster",
  /** A grant on the proposal that includes the field; named only when the viewer may read it. */
  ProposalField: "proposalField",
} as const;

const RecordAccessRemedyZ = z.object({
  rule: z.string(),
  permissionKind: z.string().nullish(),
  offered: z.boolean(),
  proposalId: z.string().nullish(),
  proposalField: z.string().nullish(),
});

const ForumAccessReasonZ = z.object({
  permissionId: z.string(),
  rule: z.string(),
  accessLevel: z.number().int(),
  grantorUserId: z.string().nullish(),
  source: z.string().nullish(),
  visibleUntil: z.string().nullish(),
  parentPermissionIds: z.array(z.string()).default([]),
});

const ForumAccessCheckZ = z.object({
  forumId: z.string(),
  userId: z.string(),
  canOpen: z.boolean(),
  accessLevel: z.number().int().nullish(),
  visibleUntil: z.string().nullish(),
  reasons: z.array(ForumAccessReasonZ).default([]),
  remedies: z.array(RecordAccessRemedyZ).default([]),
});

const RecordAccessReasonZ = z.object({
  rule: z.string(),
  permissionId: z.string().nullish(),
  grantorUserId: z.string().nullish(),
  parentPermissionIds: z.array(z.string()).default([]),
  source: z.string().nullish(),
  sourceId: z.string().nullish(),
  visibleUntil: z.string().nullish(),
});

const RecordAccessCheckZ = z.object({
  recordId: z.string(),
  userId: z.string(),
  canOpen: z.boolean(),
  withheldBy: z.string().nullish(),
  visibleUntil: z.string().nullish(),
  reasons: z.array(RecordAccessReasonZ).default([]),
  remedies: z.array(RecordAccessRemedyZ).default([]),
});

const RecordActionCheckZ = z.object({
  recordId: z.string(),
  userId: z.string(),
  action: z.string(),
  allowed: z.boolean(),
  canOpen: z.boolean(),
  refusedBy: z.string().nullish(),
  withheldBy: z.string().nullish(),
  restrictedFields: z.array(z.string()).default([]),
  awaitedApprovers: z.array(z.string()).default([]),
  awaitsOtherOrganization: z.boolean().default(false),
  reasons: z.array(RecordAccessReasonZ.extend({ accessLevel: z.number().int().nullish() })).default([]),
});

/** One grant or rule that lets the person reach the record. */
export type RecordAccessReason = Readonly<{
  /** Unique within one answer: the grant's id, or the rule and its place where the rule is not a grant. */
  key: string;
  rule: string;
  /** The grant's level. Only a conversation's answer says it. */
  accessLevel?: number;
  grantorUserId?: string;
  /** For a conversation, the roster's note; for another record, `proposal` or `workAgreement`. */
  source?: string;
  sourceId?: string;
}>;

/** One change that would open a record the answer refuses. */
export type RecordAccessRemedy = Readonly<{
  rule: string;
  /** The slug of the kind of grant that would open it. */
  permissionKind?: string;
  /** Whether this viewer may make it from the answer. */
  offered: boolean;
  /** For a proposal field the viewer may read: the proposal, and the field left out. */
  proposalId?: string;
  proposalField?: string;
}>;

function parseRemedy(remedy: z.infer<typeof RecordAccessRemedyZ>): RecordAccessRemedy {
  return {
    rule: remedy.rule,
    permissionKind: remedy.permissionKind ?? undefined,
    offered: remedy.offered,
    proposalId: remedy.proposalId ?? undefined,
    proposalField: remedy.proposalField ?? undefined,
  };
}

/** The server's answer to "can this person open this record?", the same for every domain. */
export type RecordAccessAnswer = Readonly<{
  recordId: string;
  userId: string;
  canOpen: boolean;
  /** The level the person opens it at. Only a conversation's answer says it. */
  accessLevel?: number;
  /** Set when the person reads the record only as it was when they left the work. */
  visibleUntil?: string;
  /** Set when the reasons reach the record and something else still refuses it. */
  withheldBy?: string;
  reasons: readonly RecordAccessReason[];
  /** What would open it when `canOpen` is false; empty otherwise. */
  remedies: readonly RecordAccessRemedy[];
}>;

/** The server's answer to "can this person do this action on this record?" (#6830). */
export type RecordActionAnswer = Readonly<{
  recordId: string;
  userId: string;
  action: string;
  /** What the action's own check decides. */
  allowed: boolean;
  /** What the record's read decides. */
  canOpen: boolean;
  /** Set when the person opens the record and the action is still refused: a `RecordActionRefusal`. */
  refusedBy?: string;
  /** Set when the reasons reach the record and the read still refuses it: a `RecordAccessWithholding`. */
  withheldBy?: string;
  /** For an allowed edit of a proposal, the fields the person still may not change. */
  restrictedFields: readonly string[];
  /** For an approval the status does not wait on the person for: the user ids, in the viewer's organization, it waits for. */
  awaitedApprovers: readonly string[];
  /** For that refusal: whether it also waits for another organization's people, whom the server does not name. */
  awaitsOtherOrganization: boolean;
  /** When allowed, what lets them do it; when refused, what lets them open the record. */
  reasons: readonly RecordAccessReason[];
}>;

export type RecordAccessCheckArgs = Readonly<{ domain: CheckedRecordDomain; recordId: string; userId: string }>;

export type RecordActionCheckArgs = RecordAccessCheckArgs & Readonly<{ action: Exclude<RecordAction, "open"> }>;

/** Invite on the organization: the person asked about, in the viewer's own organization. */
export type MemberInvitationCheckArgs = Readonly<{ legalEntityId: string; userId: string }>;

/** Where each domain's Check access answer is read. */
export function recordAccessCheckUrl({ domain, recordId, userId }: RecordAccessCheckArgs): string {
  switch (domain) {
    case "forums":
      return `messaging/forums/${recordId}/access/${userId}`;
    case "documents":
      return `documents/${recordId}/access/${userId}`;
    case "proposals":
      return `work/proposals/${recordId}/access/${userId}`;
    case "work-agreements":
      return `work/${recordId}/access/${userId}`;
    case "calendar-events":
      return `calendar/events/${recordId}/access/${userId}`;
    case "organization-agreements":
      return `legal-entities/entity-agreements/${recordId}/access/${userId}`;
    case "fee-schedule-templates":
      return `work/fee-schedule-templates/${recordId}/access/${userId}`;
    case "team-templates":
      return `work/team-templates/${recordId}/access/${userId}`;
  }
}

/** Reads a domain's answer into the one shape the page draws. */
export function parseRecordAccessAnswer(domain: CheckedRecordDomain, response: unknown): RecordAccessAnswer {
  if (domain === "forums") {
    const check = ForumAccessCheckZ.parse(response);
    return {
      recordId: check.forumId,
      userId: check.userId,
      canOpen: check.canOpen,
      accessLevel: check.accessLevel ?? undefined,
      visibleUntil: check.visibleUntil ?? undefined,
      reasons: check.reasons.map((reason) => ({
        key: reason.permissionId,
        rule: reason.rule,
        accessLevel: reason.accessLevel,
        grantorUserId: reason.grantorUserId ?? undefined,
        source: reason.source ?? undefined,
      })),
      remedies: check.remedies.map(parseRemedy),
    };
  }
  const check = RecordAccessCheckZ.parse(response);
  return {
    recordId: check.recordId,
    userId: check.userId,
    canOpen: check.canOpen,
    visibleUntil: check.visibleUntil ?? undefined,
    withheldBy: check.withheldBy ?? undefined,
    reasons: check.reasons.map((reason, index) => ({
      key: reason.permissionId ?? `${reason.rule}-${index}`,
      rule: reason.rule,
      grantorUserId: reason.grantorUserId ?? undefined,
      source: reason.source ?? undefined,
      sourceId: reason.sourceId ?? undefined,
    })),
    remedies: check.remedies.map(parseRemedy),
  };
}

/** Where an action's answer is read: beside the record's Check access, named for the action. */
export function recordActionCheckUrl({ action, ...record }: RecordActionCheckArgs): string {
  return `${recordAccessCheckUrl(record)}/${action}`;
}

/** Reads an action's answer, which every domain gives in `RecordActionCheckResponse`'s shape. */
export function parseRecordActionAnswer(response: unknown): RecordActionAnswer {
  const check = RecordActionCheckZ.parse(response);
  return {
    recordId: check.recordId,
    userId: check.userId,
    action: check.action,
    allowed: check.allowed,
    canOpen: check.canOpen,
    refusedBy: check.refusedBy ?? undefined,
    withheldBy: check.withheldBy ?? undefined,
    restrictedFields: check.restrictedFields,
    awaitedApprovers: check.awaitedApprovers,
    awaitsOtherOrganization: check.awaitsOtherOrganization,
    reasons: check.reasons.map((reason, index) => ({
      key: reason.permissionId ?? `${reason.rule}-${index}`,
      rule: reason.rule,
      accessLevel: reason.accessLevel ?? undefined,
      grantorUserId: reason.grantorUserId ?? undefined,
      source: reason.source ?? undefined,
      sourceId: reason.sourceId ?? undefined,
    })),
  };
}

export const recordAccessApi = api.injectEndpoints({
  endpoints: (build) => ({
    checkRecordAction: build.query<RecordActionAnswer, RecordActionCheckArgs>({
      query: (args) => ({ url: recordActionCheckUrl(args), method: "GET" }),
      transformResponse: (response: unknown) => parseRecordActionAnswer(response),
      // Read again when a grant changes, as the Open answer is.
      providesTags: (_result, _error, { userId }) => [
        { type: "Permission" as const, id: "LIST" },
        permissionCacheTags.grantee(userId),
      ],
    }),
    checkMemberInvitationAccess: build.query<RecordActionAnswer, MemberInvitationCheckArgs>({
      query: ({ legalEntityId, userId }) => ({ url: `legal-entities/${legalEntityId}/access/${userId}/invite`, method: "GET" }),
      transformResponse: (response: unknown) => parseRecordActionAnswer(response),
      // The answer is the person's Member invitations grants, so a change to any of them changes it.
      providesTags: (_result, _error, { userId }) => [
        { type: "Permission" as const, id: "LIST" },
        permissionCacheTags.grantee(userId),
      ],
    }),
    checkRecordAccess: build.query<RecordAccessAnswer, RecordAccessCheckArgs>({
      query: (args) => ({ url: recordAccessCheckUrl(args), method: "GET" }),
      transformResponse: (response: unknown, _meta, args) => parseRecordAccessAnswer(args.domain, response),
      // A grant changed on the page changes the answer, so it is read again with the lists. The
      // answer is the person's own grants, so a change to any of them, made elsewhere, does too (#6785).
      providesTags: (_result, _error, { userId }) => [
        { type: "Permission" as const, id: "LIST" },
        permissionCacheTags.grantee(userId),
      ],
    }),
  }),
});

export const { useCheckRecordAccessQuery, useCheckRecordActionQuery, useCheckMemberInvitationAccessQuery } = recordAccessApi;
